import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import Draggable16 from '@carbon/icons-react/es/draggable/16';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DateType, ProjectId } from 'api/models';
import { ProjectPhase, ShiftTasksPayload } from 'api/project';
import { Task } from 'api/tasks';
import { IconShell } from 'components/atoms/icon-shell';
import { toastService } from 'components/molecules/toast';
import { TaskName } from 'components/organisms/templates/commons/name-field';
import { Tree } from 'components/organisms/templates/projects/commons/rearrange/components';
import { useTransformTabularData } from 'data/projects';
import { ProjectTemplateGanttData } from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { byStartDate } from 'utils/comparators/by-start-date';
import { Conflict } from './types';
import {
  getDepthOfTask,
  getParent,
  getSibilings,
  insertTaskIntoParent,
  removeTaskFromParent,
  toHashMap,
  toTreeData,
  isTaskDatesOutOfParentDateRange,
  handleDateConflictsArray,
  getTasksPayload,
} from './utils';

interface Props {
  currentTaskId: number;
  projectId: ProjectId;
  setActions: React.Dispatch<React.SetStateAction<ShiftTasksPayload>>;
  actions: ShiftTasksPayload;
  conflicts: Conflict[];
  setConflicts: React.Dispatch<React.SetStateAction<Conflict[]>>;
  setHaveConflicts: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RearrangeTree: FC<Props> = ({
  currentTaskId: taskId,
  projectId,
  setActions,
  actions,
  conflicts,
  setConflicts,
  setHaveConflicts,
}) => {
  const tabData = useTransformTabularData(projectId);
  const [dataAsHashMap, setUpdatedHashMap] = useState(toHashMap(tabData));

  const handleSetPayload = useCallback(
    (
      oldTask,
      oldPhase,
      updatedData,
      droppedOn,
      draggedTask,
      parentTaskDates,
    ) => {
      let phasePayload: ProjectPhase[] = [...actions.updatedPhases];
      let taskPayload: Task[] = [...actions.updatedTasks];

      if (oldPhase) {
        phasePayload = phasePayload.filter(
          (item) => item.projectPhaseId !== oldPhase,
        );
        phasePayload = [
          ...phasePayload,
          {
            ...updatedData.projectPhases[oldPhase].data,
          },
        ];
      }

      if (oldTask) {
        const task = getTasksPayload(
          updatedData,
          oldTask,
          parentTaskDates,
          false,
        ) as unknown as Task;

        taskPayload = taskPayload.filter((item) => item.taskId !== oldTask);

        taskPayload = [...taskPayload, task];
      }
      if (droppedOn.type === 'PROJECT_PHASE') {
        phasePayload = phasePayload.filter(
          (item) => item.projectPhaseId !== droppedOn.id,
        );
        phasePayload = [
          ...phasePayload,
          {
            ...updatedData.projectPhases[droppedOn.id].data,
          },
        ];
      } else {
        taskPayload = taskPayload.filter(
          (item) => item.taskId !== droppedOn.id,
        );
        const task = getTasksPayload(
          updatedData,
          droppedOn.id,
          parentTaskDates,
          false,
        ) as unknown as Task;

        taskPayload = [...taskPayload, task];
      }

      const task = getTasksPayload(
        updatedData,
        draggedTask.id,
        parentTaskDates,
        true,
      ) as unknown as Task;

      taskPayload = taskPayload.filter((item) => item.taskId !== task.taskId);
      setActions({
        updatedPhases: phasePayload,
        updatedTasks: [...taskPayload, task],
      });
    },
    [actions, setActions],
  );

  const title = useTranslation(
    'templates.rearrange_tasks.drop_not_allowed.title',
  );
  const subtasksLimitReached = useTranslation(
    'templates.rearrange_tasks.drop_not_allowed.subtasks_limit',
  );
  const notAllowedAsPhase = useTranslation(
    'templates.rearrange_tasks.drop_not_allowed.on_phase_level',
  );

  const notAllowedInPrivatePhase = useTranslation(
    'tasks.rearrange.not_allowed_in_private_phase',
  );

  const treeData = useMemo(() => toTreeData(dataAsHashMap), [dataAsHashMap]);

  const showNotAllowedAsPhaseErrorMessage = useCallback(
    () =>
      toastService({
        title,
        message: notAllowedAsPhase,
      }).error(),
    [notAllowedAsPhase, title],
  );

  const showNotAllowedDropInPrivatePhase = useCallback(
    () =>
      toastService({
        title,
        message: notAllowedInPrivatePhase,
      }).error(),
    [notAllowedInPrivatePhase, title],
  );

  const onDrop = useCallback(
    ({ node: dropNode, dragNode }) => {
      const draggedTask = dragNode;
      const droppedOn = dropNode;

      const DatesArray: [[DateType, DateType], [DateType, DateType]] =
        droppedOn.type === 'PROJECT_PHASE'
          ? [
              [draggedTask.startDate, draggedTask.dueDate],
              [droppedOn.startDate, droppedOn.dueDate],
            ]
          : [
              [draggedTask.startDate, draggedTask.dueDate],
              [
                dataAsHashMap.projectPhases[
                  droppedOn.data.projectPhase.projectPhaseId
                ].startDate,
                dataAsHashMap.projectPhases[
                  droppedOn.data.projectPhase.projectPhaseId
                ].dueDate,
              ],
            ];

      const droppedPhaseId =
        droppedOn.type === 'PROJECT_PHASE'
          ? droppedOn.data.projectPhaseId
          : droppedOn.data.projectPhase.projectPhaseId;

      try {
        if (dropNode.dragOver) {
          // dropped over the node, so add it as first child node

          if (droppedOn.private && !draggedTask.private) {
            showNotAllowedDropInPrivatePhase();

            return;
          }

          if (isTaskDatesOutOfParentDateRange(DatesArray[0], DatesArray[1])) {
            const arr = handleDateConflictsArray(
              conflicts,
              droppedPhaseId,
              draggedTask,
              dataAsHashMap,
              droppedOn.type === 'TASK'
                ? [droppedOn.data.startDate, droppedOn.data.dueDate]
                : [],
            );

            setConflicts(arr);

            setHaveConflicts(true);
          }
          let oldTask;
          let oldPhase;

          if (dataAsHashMap.tasks[draggedTask.id].data.parentTask) {
            oldTask = dataAsHashMap.tasks[draggedTask.id].data.parentTask;
          } else {
            oldPhase =
              dataAsHashMap.tasks[draggedTask.id].data.projectPhase
                .projectPhaseId;
          }

          const updated = removeTaskFromParent(dataAsHashMap, draggedTask);
          const { data } = insertTaskIntoParent(
            updated,
            droppedOn,
            draggedTask.id,
            0,
          );

          setUpdatedHashMap(data);
          handleSetPayload(
            oldTask,
            oldPhase,
            data,
            droppedOn,
            draggedTask,
            droppedOn.type === 'TASK'
              ? [droppedOn.data.startDate, droppedOn.data.dueDate]
              : ['', ''],
          );

          return;
        }
        if (droppedOn.type !== 'TASK') {
          showNotAllowedAsPhaseErrorMessage();

          return;
        }
        if (
          droppedOn.private !== draggedTask.private &&
          dataAsHashMap.projectPhases[
            droppedOn.data.projectPhase.projectPhaseId
          ].private
        ) {
          showNotAllowedDropInPrivatePhase();

          return;
        }
        if (dropNode.dragOverGapBottom) {
          // dropped below task, so add it as next sibiling
          if (isTaskDatesOutOfParentDateRange(DatesArray[0], DatesArray[1])) {
            const parent = getParent(dataAsHashMap, droppedOn);

            const arr = handleDateConflictsArray(
              conflicts,
              droppedPhaseId,
              draggedTask,
              dataAsHashMap,
              parent.type === 'TASK'
                ? [parent.data.startDate, parent.data.dueDate]
                : [],
            );

            setConflicts(arr);

            setHaveConflicts(true);
          }
          let oldTask;
          let oldPhase;

          if (dataAsHashMap.tasks[draggedTask.id].data.parentTask) {
            oldTask = dataAsHashMap.tasks[draggedTask.id].data.parentTask;
          } else {
            oldPhase =
              dataAsHashMap.tasks[draggedTask.id].data.projectPhase
                .projectPhaseId;
          }

          const updated = removeTaskFromParent(dataAsHashMap, draggedTask);
          const subTasks = getSibilings(updated, droppedOn);
          const insertAt = subTasks.indexOf(droppedOn.id) + 1;
          const parent = getParent(updated, droppedOn);
          const { data } = insertTaskIntoParent(
            updated,
            parent,
            draggedTask.id,
            insertAt,
          );

          setUpdatedHashMap(data);
          handleSetPayload(
            oldTask,
            oldPhase,
            data,
            parent,
            draggedTask,
            parent.type === 'TASK'
              ? [parent.data.startDate, parent.data.dueDate]
              : ['', ''],
          );
        } else {
          if (droppedOn.private !== draggedTask.private) {
            showNotAllowedDropInPrivatePhase();

            return;
          }

          if (isTaskDatesOutOfParentDateRange(DatesArray[0], DatesArray[1])) {
            const parent = getParent(dataAsHashMap, droppedOn);

            const arr = handleDateConflictsArray(
              conflicts,
              droppedPhaseId,
              draggedTask,
              dataAsHashMap,
              parent.type === 'TASK'
                ? [parent.data.startDate, parent.data.dueDate]
                : [],
            );

            setConflicts(arr);

            setHaveConflicts(true);
          }
          let oldTask;
          let oldPhase;

          if (dataAsHashMap.tasks[draggedTask.id].data.parentTask) {
            oldTask = dataAsHashMap.tasks[draggedTask.id].data.parentTask;
          } else {
            oldPhase =
              dataAsHashMap.tasks[draggedTask.id].data.projectPhase
                .projectPhaseId;
          }
          const dropPos = dropNode.pos.split('-');
          const insertAt = parseInt(dropPos[dropPos.length - 1], 10) + 1;
          const updated = removeTaskFromParent(dataAsHashMap, draggedTask);

          const parent = getParent(updated, droppedOn);

          const { data } = insertTaskIntoParent(
            updated,
            getParent(updated, droppedOn),
            draggedTask.id,
            insertAt,
          );

          setUpdatedHashMap(data);
          handleSetPayload(
            oldTask,
            oldPhase,
            data,
            getParent(updated, droppedOn),
            draggedTask,
            parent.type === 'TASK'
              ? [parent.data.startDate, parent.data.dueDate]
              : ['', ''],
          );
        }
      } catch (error) {
        logger.error(
          `error in rearrange task with taskId as ${draggedTask.id} to ${droppedOn.id} `,
          { error },
        );
      }
    },
    [
      conflicts,
      dataAsHashMap,
      handleSetPayload,
      setConflicts,
      setHaveConflicts,
      showNotAllowedAsPhaseErrorMessage,
      showNotAllowedDropInPrivatePhase,
    ],
  );

  const [initialized, setInitialized] = useState(false);
  const ref = useRef<any>(null);
  const taskKey = `TASK-${taskId}`;
  const [selectedKeys, setSelectedKeys] = useState<string[]>([taskKey]);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (initialized) {
      return;
    }
    ref.current?.scrollTo({ key: taskKey });
    setInitialized(true);
  }, [initialized, ref, taskKey]);
  const windowHeight = window.innerHeight;
  const modalFooterHeight = 104;
  const modalHeaderHeight = 58;
  const treeHeight =
    windowHeight * 0.75 - modalFooterHeight - modalHeaderHeight;

  const treeClass = useMemo(() => {
    const id = parseInt(selectedKeys[0].replace('TASK-', ''), 10);
    const draggedTaskDepth = getDepthOfTask(dataAsHashMap, id);

    return `dragged-task-depth-${draggedTaskDepth} ${
      isDragging ? 'dragging' : ''
    }`;
  }, [dataAsHashMap, isDragging, selectedKeys]);

  return (
    <Tree
      errorMessage={{
        dropOnPhaseLevel: notAllowedAsPhase,
        subTasksLimitReached: subtasksLimitReached,
      }}
      ref={ref}
      treeData={treeData.sort(byStartDate)}
      defaultExpandAll
      blockNode
      className={treeClass}
      height={treeHeight}
      onDragStart={({ event, node }) => {
        if (
          (node as unknown as ProjectTemplateGanttData).type === 'PROJECT_PHASE'
        ) {
          event.preventDefault();

          return;
        }
        setIsDragging(true);
        setSelectedKeys([node.key.toString()]);
      }}
      onDragEnd={() => setIsDragging(false)}
      onDragOver={({ node: { key } }) => {
        ref.current?.scrollTo({ key, offset: 10 });
      }}
      showIcon
      icon={(props) =>
        (props as unknown as ProjectTemplateGanttData).type === 'TASK' ? (
          <Draggable16 />
        ) : null
      }
      switcherIcon={<IconShell icon={<CaretDown16 />} />}
      onDrop={onDrop}
      draggable
      selectedKeys={selectedKeys}
      titleRender={(data) => <TaskName taskName={data.title as string} />}
    />
  );
};
